import './mfa-verify.css';

import EventEmitter from "eventemitter3";
import { Context } from '../../../models/context';
import { DataLayerService } from '../../../services/data-layer-service';
import { LocalizationService } from '../../../services/localization-service';
import { DialogCreators } from '../../../utils/dialog-creators';
import { Util } from '../../../utils/util';
import { ContactMask } from '../../../utils/contact-mask';
import { LoaderSpinner } from '../../shared/LoaderSpinner/loader-spinner';
import { Modal } from '../modal';
import { IMfaPrompt, IMfaPromptOptions, MfaPromptPurpose, MfaVerifyOutcome } from './mfa-verify.interface';

const CODE_PATTERN = /^\d{6}$/;
const RESENT_NOTICE_MS = 4000;
const RESEND_COOLDOWN_MS = 30000;
const ENDING_OUTCOME_MESSAGES: Partial<Record<MfaVerifyOutcome, string>> = {
    [MfaVerifyOutcome.Expired]: "MfaChallengeExpired",
    [MfaVerifyOutcome.TooManyAttempts]: "MfaTooManyAttempts",
    [MfaVerifyOutcome.Unavailable]: "UnexpectedError500"
};
const PURPOSE_TAGS: Record<MfaPromptPurpose, { subtitle: string, verifyButton: string, expired: string }> = {
    [MfaPromptPurpose.SignIn]: { subtitle: "MfaVerifySubtitle", verifyButton: "MfaVerifyAndSignIn", expired: "MfaChallengeExpired" },
    [MfaPromptPurpose.AccountAction]: { subtitle: "MfaVerifySubtitleAccount", verifyButton: "MfaVerify", expired: "MfaChallengeExpiredAccount" }
};

export class MfaVerifyService extends Modal implements IMfaPrompt {
    private _options: IMfaPromptOptions | null = null;
    private _resolve: ((verified: boolean) => void) | null = null;
    private _contact: string = "";
    private _resentTimer: number | null = null;
    private _resendCooldownTimer: number | null = null;
    private _busy: boolean = false;
    private _pendingVerify: Promise<MfaVerifyOutcome> | null = null;

    constructor(eventBus: EventEmitter, context: Context, localizationService: LocalizationService, dataLayer: DataLayerService) {
        super("modal_mfa_verify", eventBus, context, dataLayer, localizationService, {
            hasCloseButton: true,
            ariaLabel: localizationService.names("MfaVerifyTitle"),
            onClose: () => this._settleOnClose()
        });
    }

    public override init(): void {
        super.init();
        this._setEventListeners();
    }

    public promptAsync(options: IMfaPromptOptions): Promise<boolean> {
        this._settle(false);
        this._options = options;
        this._contact = "";
        this._showStep1();
        this.open();
        Util.ensureAnnouncerPresent();

        return new Promise<boolean>(resolve => { this._resolve = resolve; });
    }

    public showEndingMessage(outcome: MfaVerifyOutcome): void {
        const message = outcome === MfaVerifyOutcome.Expired ? this._purposeTags().expired : ENDING_OUTCOME_MESSAGES[outcome];
        if (message)
            DialogCreators.messageBoxOk(this._localizationService, this._dataLayer, this._localizationService.names(message), true);
    }

    /** Masks what the customer typed (never the account's own contact), so Step 2 reveals nothing about a match. */
    public static maskContact(contact: string): string {
        const value = contact.trim();
        if (value.indexOf("@") > 0)
            return ContactMask.email(value);

        const digits = value.replaceAll(/\D/g, "");
        return digits.length >= 4 ? `***-***-${digits.slice(-4)}` : "***";
    }

    protected _setEventListeners(): void {
        Util.setElement("onclick", "btn_mfa_continue", this._handleContinueAsync);
        Util.setElement("onclick", "btn_mfa_verify", this._handleVerifyAsync);
        Util.setElement("onclick", "btn_mfa_resend", this._handleResendAsync);

        this._onEnter("txt_mfa_contact", this._handleContinueAsync);
        this._onEnter("txt_mfa_code", this._handleVerifyAsync);

        const contact = document.getElementById("txt_mfa_contact") as HTMLInputElement | null;
        if (contact)
            contact.oninput = () => Util.hideElementErrors("txt_mfa_contact", "spn_mfa_contact_error");

        const code = document.getElementById("txt_mfa_code") as HTMLInputElement | null;
        if (code)
            code.oninput = () => Util.hideElementErrors("txt_mfa_code", "spn_mfa_code_error");
    }

    private _onEnter(inputId: string, handler: () => Promise<void>): void {
        const input = document.getElementById(inputId) as HTMLInputElement | null;
        if (!input)
            return;

        input.onkeydown = async (event: KeyboardEvent) => {
            if (event.key !== "Enter")
                return;

            event.preventDefault();
            await handler();
        };
    }

    private readonly _handleContinueAsync = (): Promise<void> => this._exclusiveAsync(async () => {
        if (!this._options)
            return;

        const contact = this._inputValue("txt_mfa_contact");
        if (!contact) {
            this._showError("txt_mfa_contact", "spn_mfa_contact_error");
            return;
        }

        this._contact = contact;
        await this._sendCodeAsync();
        this._showStep2();
    });

    private readonly _handleVerifyAsync = (): Promise<void> => this._exclusiveAsync(async () => {
        if (!this._options)
            return;

        const code = this._inputValue("txt_mfa_code");
        if (!CODE_PATTERN.test(code)) {
            this._showError("txt_mfa_code", "spn_mfa_code_error");
            return;
        }

        const trustBrowser = this._isSignIn() && this._isChecked("chk_mfa_trust");
        const loader = new LoaderSpinner();
        this._pendingVerify = this._options.verifyCode(code, trustBrowser).catch(() => MfaVerifyOutcome.InvalidCode);
        const outcome = await this._pendingVerify;
        this._pendingVerify = null;
        loader?.destroy();

        // Closed mid-request: _settleOnClose already resolved the prompt from this outcome.
        if (!this._resolve)
            return;

        if (outcome === MfaVerifyOutcome.Verified) {
            this._settle(true);
            this.close();
            return;
        }

        if (ENDING_OUTCOME_MESSAGES[outcome]) {
            this.close();
            this.showEndingMessage(outcome);
            return;
        }

        this._showError("txt_mfa_code", "spn_mfa_code_error");
        (document.getElementById("txt_mfa_code") as HTMLInputElement | null)?.select();
    });

    private readonly _handleResendAsync = (): Promise<void> => this._exclusiveAsync(async () => {
        if (!this._options || !this._contact || this._isResendCoolingDown())
            return;

        Util.hideElementErrors("txt_mfa_code", "spn_mfa_code_error");
        await this._sendCodeAsync();

        Util.showElement("p_mfa_resent");
        if (this._resentTimer !== null)
            window.clearTimeout(this._resentTimer);
        this._resentTimer = window.setTimeout(() => Util.hideElement("p_mfa_resent"), RESENT_NOTICE_MS);
    });

    private async _exclusiveAsync(action: () => Promise<void>): Promise<void> {
        if (this._busy)
            return;

        this._busy = true;
        try {
            await action();
        } finally {
            this._busy = false;
        }
    }

    // A failed send is deliberately indistinguishable from a successful one (enumeration protection).
    private async _sendCodeAsync(): Promise<void> {
        const loader = new LoaderSpinner();
        await this._options?.sendCode(this._contact).catch(() => undefined);
        loader?.destroy();
        this._startResendCooldown();
    }

    private _startResendCooldown(): void {
        this._clearResendCooldown();
        this._setResendDisabled(true);
        this._resendCooldownTimer = window.setTimeout(() => {
            this._resendCooldownTimer = null;
            this._setResendDisabled(false);
        }, RESEND_COOLDOWN_MS);
    }

    private _clearResendCooldown(): void {
        if (this._resendCooldownTimer !== null)
            window.clearTimeout(this._resendCooldownTimer);
        this._resendCooldownTimer = null;
    }

    private _isResendCoolingDown(): boolean {
        return this._resendCooldownTimer !== null;
    }

    private _setResendDisabled(disabled: boolean): void {
        const resend = document.getElementById("btn_mfa_resend") as HTMLButtonElement | null;
        if (resend)
            resend.disabled = disabled;
    }

    private _showStep1(): void {
        this._resetInputs();
        this._clearResendCooldown();
        this._setResendDisabled(false);
        Util.hideElementErrors("txt_mfa_contact", "spn_mfa_contact_error");
        Util.hideElementErrors("txt_mfa_code", "spn_mfa_code_error");
        Util.hideElement("p_mfa_resent");
        this._applyPurposeText();
        Util.hideElement("div_mfa_step2");
        Util.showElement("div_mfa_step1");
        setTimeout(() => document.getElementById("txt_mfa_contact")?.focus(), 100);
    }

    private _showStep2(): void {
        Util.setElement("innerText", "p_mfa_sent_to", this._localizationService.names("MfaSentTo", undefined, MfaVerifyService.maskContact(this._contact)));

        const trustToggle = this._isSignIn() ? Util.showElement : Util.hideElement;
        trustToggle("div_mfa_trust");

        Util.hideElement("div_mfa_step1");
        Util.showElement("div_mfa_step2");
        setTimeout(() => document.getElementById("txt_mfa_code")?.focus(), 100);
    }

    private _purposeTags(): { subtitle: string, verifyButton: string, expired: string } {
        return PURPOSE_TAGS[this._options?.purpose ?? MfaPromptPurpose.SignIn];
    }

    private _isSignIn(): boolean {
        return this._options?.purpose === MfaPromptPurpose.SignIn;
    }

    // The ltag swap keeps a language change from restoring the markup's sign-in copy.
    private _applyPurposeText(): void {
        const tags = this._purposeTags();
        for (const [id, tag] of [["p_mfa_subtitle", tags.subtitle], ["btn_mfa_verify", tags.verifyButton]]) {
            const element = document.getElementById(id);
            if (!element)
                continue;

            element.setAttribute("ltag", tag);
            element.innerText = this._localizationService.names(tag);
        }
    }

    private _resetInputs(): void {
        for (const inputId of ["txt_mfa_contact", "txt_mfa_code"]) {
            const input = document.getElementById(inputId) as HTMLInputElement | null;
            if (input)
                input.value = "";
        }

        const trust = document.getElementById("chk_mfa_trust") as HTMLInputElement | null;
        if (trust)
            trust.checked = false;
    }

    private _showError(inputId: string, errorId: string): void {
        Util.showElement(errorId);
        const input = document.getElementById(inputId);
        input?.classList.add("invalid-input");
        input?.setAttribute("aria-describedby", errorId);
        Util.announceErrors(Util.collectVisibleErrors("modal_mfa_verify"));
    }

    private _inputValue(inputId: string): string {
        return (document.getElementById(inputId) as HTMLInputElement | null)?.value.trim() ?? "";
    }

    private _isChecked(inputId: string): boolean {
        return (document.getElementById(inputId) as HTMLInputElement | null)?.checked ?? false;
    }

    // A verify already sent may have issued session/trust tokens, so honour its outcome rather than discarding it.
    private _settleOnClose(): void {
        const pending = this._pendingVerify;
        if (!pending) {
            this._settle(false);
            return;
        }

        const resolve = this._resolve;
        this._resolve = null;
        void pending.then(outcome => resolve?.(outcome === MfaVerifyOutcome.Verified));
    }

    private _settle(verified: boolean): void {
        const resolve = this._resolve;
        this._resolve = null;
        resolve?.(verified);
    }
}
